// "Filme" do produto no topo da home: 4 capítulos (conectar, entender, agir, acompanhar) num mesmo cartão.
// Tudo é ilustração com dados de exemplo (o cartão diz isso). Cada capítulo remonta e roda suas animações CSS;
// o fim da barra de progresso do capítulo ativo avança para o próximo, então pausar a barra (fora da tela,
// aba escondida) pausa o filme inteiro sem relógio próprio. Com prefers-reduced-motion não avança sozinho.
import { useEffect, useState, type CSSProperties } from 'react'
import { Check, MousePointer2, Undo2, Zap } from 'lucide-react'
import { Counter, useInView, Words } from './motion'

const CHAPTERS = ['Conectar', 'Entender', 'Agir', 'Acompanhar'] as const
const CHAPTER_MS = 5200

export function HeroFilm() {
  const [ch, setCh] = useState(0)
  const [ref, inView] = useInView<HTMLDivElement>({ once: false, rootMargin: '0px' })
  const [hidden, setHidden] = useState(false)
  useEffect(() => {
    const on = () => setHidden(document.hidden)
    document.addEventListener('visibilitychange', on)
    return () => document.removeEventListener('visibilitychange', on)
  }, [])
  const playing = inView && !hidden

  return (
    <div ref={ref} className={`sh-film ${playing ? '' : 'is-paused'}`} style={{ '--chapter-ms': `${CHAPTER_MS}ms` } as CSSProperties}>
      <div className="sh-film-card" role="img" aria-label="Exemplo ilustrativo do painel do Seu Impulso">
        <div className="sh-film-bar">
          <span className="sh-dots" aria-hidden><i /><i /><i /></span>
          <span className="sh-film-title">Padaria Exemplo</span>
          <span className="sh-film-tag">Exemplo ilustrativo</span>
        </div>
        <div className="sh-film-body" key={ch}>
          {ch === 0 && <Connect />}
          {ch === 1 && <Understand />}
          {ch === 2 && <Act />}
          {ch === 3 && <Follow />}
        </div>
      </div>
      <div className="sh-film-tabs" role="tablist" aria-label="Capítulos do exemplo">
        {CHAPTERS.map((name, i) => (
          <button key={name} type="button" role="tab" aria-selected={i === ch} className={i === ch ? 'on' : i < ch ? 'done' : ''} onClick={() => setCh(i)}>
            <span className="sh-film-n">0{i + 1}</span> {name}
            <span className="sh-film-progress" aria-hidden>
              <span onAnimationEnd={i === ch ? () => setCh((c) => (c + 1) % CHAPTERS.length) : undefined} />
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}

const step = (i: number) => ({ '--s': i } as CSSProperties)

function Connect() {
  const rows: Array<[string, string]> = [['Google Ads', 'var(--c-google)'], ['Instagram e Facebook', 'var(--c-meta)'], ['TikTok', 'var(--c-tiktok)']]
  return (
    <div className="sh-ch">
      <p className="sh-ch-k">Conectando pelas telas oficiais</p>
      <div className="sh-conn">
        {rows.map(([name, color], i) => (
          <div key={name} className="sh-conn-row" style={step(i)}>
            <span className="sh-conn-dot" style={{ background: color }} />
            <span className="sh-conn-name">{name}</span>
            <span className="sh-conn-state">
              <span className="sh-spin" aria-hidden />
              <span className="sh-ok"><Check size={14} strokeWidth={3} /> 12 meses lidos</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

function Understand() {
  return (
    <div className="sh-ch">
      <p className="sh-ch-k">Últimos 30 dias</p>
      <Words as="p" immediate className="sh-ch-big" text="Você investiu R$ 1.240 e recebeu *96 contatos* no WhatsApp." />
      <div className="sh-kpis">
        <div className="sh-kpi" style={step(0)}><span>Investido</span><b><Counter immediate value={1240} prefix="R$ " /></b></div>
        <div className="sh-kpi" style={step(1)}><span>Clientes</span><b><Counter immediate value={96} /></b></div>
        <div className="sh-kpi" style={step(2)}><span>Custo por cliente</span><b><Counter immediate value={12.9} decimals={2} prefix="R$ " /></b></div>
        <div className="sh-kpi" style={step(3)}><span>Retorno estimado</span><b><Counter immediate value={4.1} decimals={1} suffix="×" /></b></div>
      </div>
    </div>
  )
}

function Act() {
  return (
    <div className="sh-ch">
      <p className="sh-ch-k">Sugestão com o porquê</p>
      <div className="sh-sug">
        <span className="sh-sug-ic"><Zap size={16} /></span>
        <div>
          <div className="sh-sug-t">Mover R$ 15/dia para “Bolos de festa”</div>
          <div className="sh-sug-d">Ela traz clientes 3× mais baratos e está limitada pelo orçamento.</div>
          <div className="sh-sug-diff"><span>R$ 20/dia</span><span aria-hidden>→</span><b>R$ 35/dia</b></div>
          <div className="sh-sug-btns">
            <span className="sh-apply">
              <span className="sh-apply-a">Aplicar</span>
              <span className="sh-apply-b"><Check size={14} strokeWidth={3} /> Aplicado</span>
              <MousePointer2 className="sh-cursor" size={20} aria-hidden />
            </span>
            <span className="sh-ghost-btn">Ver o que muda</span>
          </div>
        </div>
      </div>
      <div className="sh-toast"><Undo2 size={14} /> Registrado no histórico. Dá para desfazer.</div>
    </div>
  )
}

function Follow() {
  return (
    <div className="sh-ch">
      <p className="sh-ch-k">Custo por cliente, semana a semana</p>
      <div className="sh-follow">
        <div className="sh-follow-num"><s>R$ 12,90</s> <b>R$ 9,80</b></div>
        <svg className="sh-chart" viewBox="0 0 320 120" preserveAspectRatio="none" aria-hidden>
          <path className="sh-chart-area" d="M0 28 L46 34 L92 30 L138 52 L184 60 L230 78 L276 84 L320 92 L320 120 L0 120 Z" />
          <path className="sh-chart-line" d="M0 28 L46 34 L92 30 L138 52 L184 60 L230 78 L276 84 L320 92" pathLength={1} />
        </svg>
      </div>
      <div className="sh-pilot"><span className="sh-pulse" aria-hidden /> Piloto automático ligado, dentro do teto do mês</div>
    </div>
  )
}
